import type {
  ListaItens,
  SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import type {
  FiltrosCatalogoLocalDeTrabalho,
  OpcaoLocalDeTrabalho,
  SelecaoCatalogoLocalDeTrabalho,
} from '../catalogo-local-de-trabalho.tipos'

export const CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO =
  'srcNext.estruturaDePontoDaEmpresa.catalogoLocalDeTrabalho'

type Traduzir = (chave: string) => string

export const criarItemSeletorLocalDeTrabalho = (
  opcao: OpcaoLocalDeTrabalho,
): SeletorItem => ({
  chave: opcao.id,
  valor: opcao.name,
  ...(typeof opcao.code === 'string' && opcao.code.trim() ? { legenda: opcao.code } : {}),
})

const normalizarOpcoesSelecionadas = (
  valor: SelecaoCatalogoLocalDeTrabalho,
  selecaoMultipla: boolean,
): readonly OpcaoLocalDeTrabalho[] => {
  if (valor === null) return []
  if (!Array.isArray(valor)) return [valor as OpcaoLocalDeTrabalho]
  if (selecaoMultipla) return valor as readonly OpcaoLocalDeTrabalho[]
  return valor[0] ? [valor[0] as OpcaoLocalDeTrabalho] : []
}

export const filtrarOpcoesLocalDeTrabalho = (
  opcoes: readonly OpcaoLocalDeTrabalho[],
  filtros?: FiltrosCatalogoLocalDeTrabalho,
  predicado?: (local: OpcaoLocalDeTrabalho) => boolean,
): readonly OpcaoLocalDeTrabalho[] => {
  if ((!filtros || Object.keys(filtros).length === 0) && !predicado) return opcoes
  filtros = filtros ?? {}

  return opcoes.filter((opcao) => (
    (filtros.id === undefined || (typeof opcao.id === 'string' && filtros.id.includes(opcao.id)))
    && (filtros.active === undefined || opcao.active === filtros.active)
    && (filtros.visivel === undefined || opcao.visivel === filtros.visivel)
    && (
      filtros.empregadores === undefined
      || (Array.isArray(opcao.empregadores) && opcao.empregadores.every(id => typeof id === 'string')
        && filtros.empregadores.some((id) => (
        opcao.empregadores?.includes(id)
      )))
    )
    && (predicado === undefined || predicado(opcao))
  ))
}

export const criarEstadoSeletorCatalogoLocal = (
  opcoes: readonly OpcaoLocalDeTrabalho[],
  valor: SelecaoCatalogoLocalDeTrabalho,
  selecaoMultipla: boolean,
): ListaItens => {
  const carregados = opcoes
    .filter(opcao => typeof opcao.id === 'string' && opcao.id.trim()
      && typeof opcao.name === 'string' && opcao.name.trim())
    .map(criarItemSeletorLocalDeTrabalho)
  const itensPorId = new Map(
    carregados.map((item) => [String(item.chave), item]),
  )
  const selecionados = normalizarOpcoesSelecionadas(valor, selecaoMultipla)
    .flatMap((opcao) => {
      const item = itensPorId.get(opcao.id)
      return item ? [item] : []
    })

  return { carregados, selecionados }
}

export const extrairValorDoSeletorCatalogoLocal = (
  estado: ListaItens,
  opcoes: readonly OpcaoLocalDeTrabalho[],
  selecaoMultipla: boolean,
): SelecaoCatalogoLocalDeTrabalho => {
  const opcoesPorId = new Map(opcoes.map((opcao) => [opcao.id, opcao]))
  const idsSelecionados = [...new Set(
    estado.selecionados.map(({ chave }) => String(chave)),
  )]
  const opcoesSelecionadas = idsSelecionados.flatMap((id) => {
    const opcao = opcoesPorId.get(id)
    return opcao ? [opcao] : []
  })
  return selecaoMultipla
    ? opcoesSelecionadas
    : (opcoesSelecionadas[0] ?? null)
}

export const criarPropriedadesPontotelSeletorLocal = ({
  traduzir,
  label,
  placeholder,
  erro,
}: Readonly<{
  traduzir: Traduzir
  label?: string
  placeholder?: string
  erro: string | null
}>) => ({
  tamanho: 'M' as const,
  largura: '100%',
  tamanhoPagina: 20,
  pesquisavel: true,
  recarregarTodos: true,
  label: label
    ?? traduzir(`${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.label`),
  placeholder: placeholder
    ?? traduzir(`${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.placeholder`),
  erro,
  traducaoNenhumResultado: traduzir(
    `${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.nenhumResultado`,
  ),
  traducaoSelecionado: traduzir(
    `${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.selecionado`,
  ),
  traducaoSelecionadosPlural: traduzir(
    `${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.selecionados`,
  ),
  traducaoRecarregarTodos: traduzir(
    `${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.recarregar`,
  ),
  traducaoRecarregado: traduzir(
    `${CHAVE_I18N_CATALOGO_LOCAL_DE_TRABALHO}.recarregado`,
  ),
})
